<template>
  <section class="ctn">
    <header class="header left">
      <router-link class="paper-action" :to="{ path: '/sites' }"><heim-icon icon="arrow-back"></heim-icon><span>{{ $t('back') }}</span></router-link>
    </header>
    <heim-card>
      <header>
        <h1>{{ $t('edit') }} {{ $t('site') }}</h1>
      </header>
      <main>
        <form @submit.prevent="handleForm">
          <div class="row">
            <span class="label">{{ $t('site') }}</span>
            <div class="inputs">
              <heim-input :label="$t('name') + '*'" name="name" placeholder="e.g : Google" v-model="site.name" v-validate="'required'"></heim-input>
              <span v-show="errors.has('name')" class="help is-danger"><heim-icon class="icon" icon="error"></heim-icon>{{ errors.first('name') }}</span>
              <heim-input
                :label="$t('link') + '*'"
                name="url"
                placeholder="https://www.google.com"
                v-model="site.url"
                required="required"
                v-validate="'required|url'"
              ></heim-input>
               <span v-show="errors.has('url')" class="help is-danger"><heim-icon class="icon" icon="error"></heim-icon>{{ errors.first('url') }}</span>
            </div>
          </div>
          <div class="row">
            <span class="label">{{ $t('monitoring') }}</span>
            <div class="inputs">
              <heim-input
                :label="$t('pingInterval') + ' (ms)'"
                name="interval"
                type="number"
                placeholder="300000"
                v-model="site.interval"
                v-validate="'required'"
              ></heim-input>
              <span v-show="errors.has('interval')" class="help is-danger"><heim-icon class="icon" icon="error"></heim-icon>{{ errors.first('interval') }}</span>
              <heim-input
                :label="$t('referenceTime') + ' (ms)'"
                name="reference_time"
                type="number"
                placeholder="8000"
                v-model="site.reference_time"
              ></heim-input>
              <heim-input
                :label="$t('errorRecurrence')"
                name="nb_occurence"
                type="number"
                placeholder="2"
                v-model="site.nb_occurence"
              ></heim-input>
            </div>
          </div>
          <div class="row">
            <span class="label">{{ $t('hostingCosts') }}</span>
            <div class="inputs">
              <heim-input
                :label="$t('invoicedAmount')"
                name="hosting_cost"
                type="number"
                v-model="site.hosting_cost"
                step="0.01"
              ></heim-input>
              <div class="heim-input date-hosting">
                <div class="date-h">
                  <span class="label">{{ $t('startDate') }}</span>
                  <datepicker 
                  type="date"
                  name="hosting_date_start"
                  input-class="paper-input"
                  wrapper-class="date-input"
                  v-model="site.hosting_date_start"
                  value-key="id"
                  :disabledDates="disabledDateStart(site.hosting_date_end)"
                  ></datepicker>
                </div>
                <div class="date-h">
                    <span class="label">{{ $t('endDate') }}</span>
                    <datepicker 
                    type="date"
                    name="hosting_date_end"
                    input-class="paper-input"
                    wrapper-class="date-input"
                    v-model="site.hosting_date_end"
                    value-key="id"
                    :disabledDates="disabledDateEnd(site.hosting_date_start)"
                    ></datepicker>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <span class="label">{{ $t('type') }}</span>
            <div class="inputs">
              <heim-select
                :label="$t('type') + '*'"
                :data="siteTypes"
                label-key="type"
                value-key="id"
                v-model="site.type_id"
                required="required"
              ></heim-select>
            </div>
          </div>
          <div class="row">
            <span class="label">{{ $t('server') }}</span>
            <div class="inputs">
              <heim-select
                :label="$t('server') + '*'"
                :data="servers"
                label-key="name"
                value-key="id"
                v-model="site.server_id"
                required="required"
              ></heim-select>
            </div>
          </div>
          <div class="row">
            <span class="label">{{ $t('isEnabled') }}</span>
            <div class="inputs">
              <heim-toggle-button v-model="site.enabled"></heim-toggle-button>
            </div>
          </div>
          <footer>
            <heim-button :disabled="errors.any()" type="submit">{{ $t('save') }}</heim-button>
          </footer>
        </form>
      </main>
    </heim-card>
    <heim-card>
      <header><h1>{{ $t('endpoints') }}</h1></header>
      <main>
        <form @submit.prevent="addEndpoint">
          <div class="site-endpoints">
            <heim-input class="endpoint-url" :label="$t('link')" type="text" name="url"></heim-input>
            <heim-input class="integrity-key" :label="$t('integrityKey')" type="text" name="integrity_key"></heim-input>
            <div class="endpoint-action-button">
              <button type="submit" class="paper-action"><heim-icon class="icon" icon="add"></heim-icon><span>{{ $t('add') }}</span></button>
            </div>
          </div>
        </form>
        <form v-for="(endpoint, idx) in site.Endpoints" :key="'endpoint-'+idx" @submit.prevent="e => updateEndpoint(e, endpoint.id)">
          <div class="site-endpoints">
            <heim-input class="endpoint-url" :label="$t('link')" type="text" name="url" v-model="endpoint.url"></heim-input>
            <heim-input class="integrity-key" :label="$t('integrityKey')" type="text" name="integrity_key" v-model="endpoint.integrity_key"></heim-input>
            <div class="endpoint-action-button">
              <button type="submit" class="paper-action update"><heim-icon class="icon" icon="edit"></heim-icon><span>{{ $t('edit') }}</span></button>
            </div>
            <div class="endpoint-action-button">
              <button type="button" class="paper-action remove" @click="removeEndpoint(endpoint.id)"><heim-icon class="icon" icon="close"></heim-icon><span>{{ $t('remove') }}</span></button>
            </div>
          </div>
        </form>
      </main>
    </heim-card>
    <section class="user-section">
      <div class="site-users">
        <heim-card>
          <header>
            <h1>{{ $t('editServerAdministrators') }}</h1>
          </header>
          <main>
            <transition-group class="users" tag="section" name="slide-fade">
              <div class="user" v-for="(user, idx) in site.Users" :key="idx">
                <div class="cell profile">
                  <span class="avatar">{{user.name.charAt(0).toUpperCase()}}</span>
                </div>
                <div class="cell info">
                  <span class="label">{{user.name}}</span>
                  <div class="email"><router-link :to="{ path: `/users/${user.id}/details` }">{{user.email}}</router-link></div>
                </div>
                <div class="cell remove">
                  <button class="paper-action" @click="removeUser(user.email)"><heim-icon class="icon" icon="close"></heim-icon><span>{{ $t('remove') }}</span></button>
                </div>
                <div class="cell see">
                  <router-link class="paper-action" :to="{ path: `/users/${user.id}/details` }"><span>{{ $t('view') }}</span><heim-icon class="icon" icon="chevron-right"></heim-icon></router-link>
                </div>
              </div>
              <div v-if="site.Users && !site.Users.length" class="empty-site-admin" key="empty">
                <div class="bg"></div>
                <span class="message">{{ $t('noUsersServer') }}</span>
              </div>
            </transition-group>
          </main>
        </heim-card>
      </div>
      <div class="select-user">
        <heim-card>
          <header>
            <h1>{{ $t('search') }} {{ $t('users') }}</h1>
          </header>
          <main>
            <div class="users">
              <div class="cell">
                <div class="custom-input">
                  <heim-input type="text" :label="$t('search') + ' ' + $t('user')" name="user" :placeholder="$t('placeholderUsername')" value="" v-model="search" :icon="true"></heim-input>
                  <heim-icon-button @click.native="e => {}">
                    <heim-icon class="icon" icon="search"></heim-icon>
                  </heim-icon-button>
                </div>
              </div>
            </div>
            <div class="users user-list">
              <div class="user" v-for="(user, idx) in filteredUsers" :key="'user-'+idx"> 
                <div class="cell profile">
                  <span class="avatar">{{user.name.charAt(0).toUpperCase()}}</span>
                </div>
                <div class="cell info">
                  <span class="label">{{user.name}}</span>
                  <div class="email"><router-link to="/">{{user.email}}</router-link></div>
                </div>
                <div class="cell see">
                  <button class="paper-action" @click="addUser(user.email)"><heim-icon class="add" icon="person-add"></heim-icon><span>{{ $t('add') }}</span></button>
                </div>
              </div>
            </div>
          </main>
        </heim-card>
      </div>
    </section>
    <heim-card class="danger-zone">
      <header>
        <h1>{{ $t('dangerZone') }}</h1>
      </header>
      <main>
        <div class="row">
          <div class="lab">
            <p>{{ $t('removeSite') }}</p>
            <p><strong>{{ $t('removingSite') }}</strong></p>
          </div>
          <div class="input">
            <button @click="deleteForm" class="paper-action remove"><heim-icon class="icon" icon="close"></heim-icon><span>{{ $t('remove') }}</span></button>
          </div>
        </div>
        <v-dialog/>
      </main>
    </heim-card>
  </section>
</template>

<script>
import Vue from "vue";
import HeimInput from "@/components/form/input/text/heim-input.vue";
import HeimToggleButton from "@/components/form/input/toggle/heim-toggle-button.vue";
import HeimSelect from "@/components/form/input/select/heim-select.vue";
import HeimButton from "@/components/form/button/heim-button.vue";
import HeimIconButton from "@/components/form/button/heim-icon-button.vue";
import HeimCard from "@/components/layout/card/heim-card.vue";
import HeimIcon from "@/components/icon/heim-icon.vue";
import { mapState } from "vuex";
import fuseMixin from "@/mixins/fuse";
import VeeValidate from "vee-validate";
import Datepicker from "vuejs-datepicker";
import VModal from "vue-js-modal";

Vue.use(VModal, { dialog: true });
Vue.use(VeeValidate);

export default {
  name: "SitesEdit",
  components: {
    HeimInput,
    HeimToggleButton,
    HeimSelect,
    HeimButton,
    HeimIconButton,
    HeimCard,
    HeimIcon,
    Datepicker
  },
  mixins: [fuseMixin],
  mounted() {
    this.$store.dispatch("getSiteTypes");
    this.$store.dispatch("getServers");
    this.$store.dispatch("getUsers");
    const id = this.$route.params.id;
    this.$store.dispatch("getSite", { id });
    this.$watch(
      vm => vm.site,
      val => {
        this.$store.dispatch("setTitle", `Edit site - ${val.name}`);
      }
    );
  },
  computed: {
    ...mapState({ site: state => state.sitesState.site }),
    ...mapState({ servers: state => state.serversState.servers }),
    ...mapState({ siteTypes: state => state.sitesState.siteTypes }),
    ...mapState({ users: state => state.usersState.users }),
    filteredUsers() {
      if (!this.site.Users) return [];
      // Check if the user if not already in the site administrator user array
      const filteredUsers = this.users.filter(
        user => this.site.Users.map(user => user.id).indexOf(user.id) == -1
      );

      return filteredUsers.filter(user => {
        return user.name.toLowerCase().includes(this.search.toLowerCase());
      });
    }
  },
  data() {
    return {
      search: ""
    };
  },
  methods: {
    async handleForm() {
      const data = await this.$store.dispatch("updateSite", {
        id: this.site.id,
        site: {
          name: this.site.name,
          url: this.site.url,
          interval: this.site.interval,
          reference_time: this.site.reference_time || null,
          nb_occurence: this.site.nb_occurence,
          type_id: this.site.type_id,
          server_id: this.site.server_id,
          enabled: this.site.enabled,
          hosting_cost: this.site.hosting_cost,
          hosting_date_start: this.site.hosting_date_start,
          hosting_date_end: this.site.hosting_date_end
        }
      });

      this.$validator.validateAll().then(result => {
        if (result && data.id) {
          this.$store.dispatch("setToastMessage", {
            message: "updateSuccessful"
          });
          this.$store.dispatch("setToastMode", {});
          this.$store.dispatch("setOpenToast", { opened: true });
          this.$router.push({ path: `/sites/${this.site.id}/details` });
          return;
        }
      });
    },
    async removeSite() {
      const data = await this.$store.dispatch("deleteSite", {
        id: this.site.id
      });
      if (data.success) {
        this.$store.dispatch("setToastMessage", {
          message: "deleteSuccessful"
        });
        this.$store.dispatch("setToastMode", {});
        this.$store.dispatch("setOpenToast", { opened: true });
        this.$router.push({ path: `/sites` });
      }
    },
    addEndpoint(e) {
      const data = this.$store.dispatch("addEndpoint", {
        url: e.target.url.value,
        integrityKey: e.target.integrity_key.value,
        siteId: this.site.id
      });
      if (data.id) {
        this.$store.dispatch("setToastMessage", {
          message: "updateSuccessful"
        });
        this.$store.dispatch("setToastMode", {});
        this.$store.dispatch("setOpenToast", { opened: true });
      }
      e.target.url.value = "";
      e.target.integrity_key.value = "";
    },
    updateEndpoint(e, id) {
      const data = this.$store.dispatch("updateEndpoint", {
        id: id,
        url: e.target.url.value,
        integrityKey: e.target.integrity_key.value,
        siteId: this.site.id
      });
      if (data.id) {
        this.$store.dispatch("setToastMessage", {
          message: "updateSuccessful"
        });
        this.$store.dispatch("setToastMode", {});
        this.$store.dispatch("setOpenToast", { opened: true });
      }
    },
    removeEndpoint(id) {
      const data = this.$store.dispatch("removeEndpoint", {
        id: id,
        siteId: this.site.id
      });
      if (data.success) {
        this.$store.dispatch("setToastMessage", {
          message: "updateSuccessful"
        });
        this.$store.dispatch("setToastMode", {});
        this.$store.dispatch("setOpenToast", { opened: true });
      }
    },
    addUser(email) {
      const data = this.$store.dispatch("addUserToSite", {
        id: this.site.id,
        email
      });
      if (data.id) {
        this.$store.dispatch("setToastMessage", {
          message: "updateSuccessful"
        });
        this.$store.dispatch("setToastMode", {});
        this.$store.dispatch("setOpenToast", { opened: true });
      }
    },
    removeUser(email) {
      const data = this.$store.dispatch("removeUserFromSite", {
        id: this.site.id,
        email
      });
      if (data.id) {
        this.$store.dispatch("setToastMessage", {
          message: "updateSuccessful"
        });
        this.$store.dispatch("setToastMode", {});
        this.$store.dispatch("setOpenToast", { opened: true });
      }
    },
    disabledDateStart(date) {
      return {
        from: new Date(date)
      };
    },
    disabledDateEnd(date) {
      return {
        to: new Date(date)
      };
    },
    deleteForm() {
      this.$modal.show("dialog", {
        title: "Suppression",
        text: "Etes-vous sur de vouloir supprimer ce site ?",
        buttons: [
          {
            title: "Oui",
            handler: () => {
              this.removeSite();
            }
          },
          {
            title: "Non",
            default: true
          }
        ]
      });
    }
  }
};
</script>
<style>
input.paper-input {
  background: var(--heim-grey-color);
  width: 100%;
  display: block;
  padding: 0.3em 0.4em;
  font-size: 0.9em;
  border-radius: 3px;
  border: solid 1px #ededed;
  outline: none;
  transition: box-shadow 0.3s var(--heim-transition);
  margin-bottom: 0.8em;
}
input.paper-input:hover,
input.paper-input:focus {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
}
.heim-input[icon] input.paper-input {
  padding-right: 32px;
}

.date-input {
  margin-bottom: 20px;
}
</style>

<style scoped>
.empty-site-admin {
  display: flex;
  flex-direction: column;
  height: 15em;
}
.empty-site-admin .bg {
  background-image: url("/img/undraw_add_user_ipe3.svg");
  background-size: 100% 100%;
  background-position: center;
  height: 100%;
  margin-bottom: 1em;
}
.empty-site-admin .message {
  justify-content: center;
  display: block;
  text-align: center;
}

.paper-card {
  margin-bottom: 1.5em;
}

.site-endpoints {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  padding: 0.8em 1em;
  border-bottom: 1px solid #eeeeee;
}

.endpoint-url {
  width: 100%;
}

.integrity-key {
  width: 100%;
}

.site-endpoints .endpoint-action-button {
  display: flex;
  align-items: center;
  margin-right: 1em;
}

.site-endpoints .endpoint-action-button .update .icon {
  margin: 0 0.5em;
}

.site-endpoints .endpoint-action-button .remove {
  color: var(--heim-red-color);
  border-color: var(--heim-red-color);
}

.user-section {
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap-reverse;
}

.site-users,
.select-user {
  width: 100%;
}

.select-user .user-list {
  height: 16em;
  overflow-y: auto;
}

.select-user .custom-input {
  position: relative;
  width: 100%;
}

.select-user .custom-input .icon-button {
  text-align: center;
  position: absolute;
  bottom: 0;
  right: 4px;
}

.users {
  width: 100%;
  padding: 0.6rem 1rem;
}

.users .user {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-radius: 8px;
}
.users .user:hover {
  background-color: var(--heim-grey-color);
}

.users .cell {
  padding: 0.3rem 0.5rem;
}

.users .user .info {
  flex: 1;
}

.users .user .info .email {
  font-size: 0.8rem;
  font-weight: 600;
  word-break: break-word;
}

.users .user .profile {
  display: none;
}

.users .user .avatar {
  font-size: 1.5rem;
  font-weight: 600;
  background-color: var(--heim-secondary-color);
  color: var(--heim-white-color);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  border-radius: 100%;
  border: solid thin var(--heim-grey-color);
  cursor: default;
}

.users .remove .paper-action {
  color: var(--heim-red-color);
  border-color: var(--heim-red-color);
}

.users .user .info .label {
  font-size: 1.2em;
  font-weight: 400;
}

.danger-zone {
  border-top: solid 4px var(--heim-red-color);
}

.danger-zone h1 {
  color: var(--heim-red-color);
}

.danger-zone .remove {
  color: var(--heim-red-color);
  border-color: var(--heim-red-color);
}

.heim-input {
  max-width: 32em;
}

.heim-input input {
  width: 100%;
}

.label {
  display: inline-block;
  font-size: 0.8em;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 0.1em;
  font-weight: 500;
}
input.paper-input {
  background: var(--heim-grey-color) !important;
  width: 100% !important;
  display: block;
  padding: 0.3em 0.4em;
  font-size: 0.9em;
  border-radius: 3px;
  border: solid 1px #ededed !important;
  outline: none;
  transition: box-shadow 0.3s var(--heim-transition);
  margin-bottom: 0.8em;
}
input.paper-input:hover,
input.paper-input:focus {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
}

.vdp-datepicker input {
  width: 100%;
}

.date-input {
  margin-bottom: 20px;
}

.date-input .paper-input {
  width: 100%;
}

.date-hosting {
  display: flex;
  flex-wrap: wrap;
}

.date-hosting .date-h {
  flex: 1;
  padding-right: 20px;
}

.date-hosting .date-h:last-child {
  padding-right: 0px;
}

/* Wide layout: when the viewport width is bigger than 460px, layout
  changes to a wide layout. */
@media (min-width: 1350px) {
  .ctn {
    margin: 1em 10em 0 10em;
  }

  .site-users {
    margin-right: 1em;
  }

  .site-users {
    width: 58%;
  }
  .select-user {
    width: 40%;
  }

  .users .user .profile {
    display: block;
  }

  .select-user .custom-input {
    width: 60%;
  }

  .endpoint-url {
    width: 60%;
    max-width: 32em;
  }

  .integrity-key {
    width: 30%;
    max-width: 32em;
  }

  .site-endpoints .endpoint-url,
  .site-endpoints .integrity-key,
  .site-endpoints .update {
    margin-right: 2em;
  }
}
</style>
